// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";

import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { ITariffCircuit } from "parameter";
import { isSet } from "utils";
import { getEnergySourceName, makeSortHeader } from "guiutils";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";
import TariffCircuitListModel from "model/tariffcircuitlist";

import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  IChartTariffNode,
} from "./ChartBase";
import TariffCircuit from "view/comp/TariffCircuit";

// sort key of table
const TABLE_NAME = "t_tariffcircuits";

interface ITariffCircuitListExtProps extends IChartBaseExtProps {
  dashboardIndex?: number;
}

interface ITariffCircuitListProps extends IChartBaseProps {
  tableSort: ITableSort | undefined;
}

interface ITariffCircuitListState extends IChartBaseState {}

interface ITariffCircuitListDispatch extends IChartBaseDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapStateToProps = (state: IState, ownProps: ITariffCircuitListExtProps) => {
  const props: ITariffCircuitListProps = {
    ...baseMapStateToProps(state, ownProps),
    tableSort: state.gui.tableSort[TABLE_NAME],
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffCircuitListDispatch = {
    ...baseMapDispatchToProps(dispatch),
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

class TariffCircuitListComponent extends ChartBaseComponent<
  ITariffCircuitListProps & ITariffCircuitListExtProps & ITariffCircuitListDispatch,
  ITariffCircuitListState
> {
  state: ITariffCircuitListState = {
    showZeroValues: true,
  };

  private renderSortedListGroup(
    tlList: IChartTariffNode[],
    showHierachy: boolean,
    showCategory: boolean,
  ) {
    const tariffCircuits = this.props.tariffCircuits;
    const sortCol = (this.props.tableSort?.column ?? "name") as keyof ITariffCircuit | "value";
    const circuitItems = tlList
      .sort((a, b) => {
        const tcA = tariffCircuits.find((curItem, curIndex) => (curItem.id ?? curIndex) === a.id);
        const tcB = tariffCircuits.find((curItem, curIndex) => (curItem.id ?? curIndex) === b.id);
        const keyA = sortCol === "value" ? a.value : tcA?.[sortCol!];
        const keyB = sortCol === "value" ? b.value : tcB?.[sortCol!];
        const sort =
          (isSet(keyA) && isSet(keyB) && keyA > keyB) || (isSet(keyA) && !isSet(keyB)) ? 1 : -1;
        return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
      })
      .map((aTL) => {
        // return a React-Component...
        return (
          <TariffCircuit
            key={aTL.id}
            id={aTL.id}
            value={aTL.value}
            maxValue={aTL.maxValue}
            isRelatedToTargetValue={aTL.isRelatedToTargetValue ?? false}
            relatedDateString={aTL.relatedDateString ?? ""}
            showHierachy={showHierachy}
            showCategory={showCategory}
            trendMember={this.props.trendMember}
            filterText={this.props.filterText}
            selectedDashboardIndex={this.props.selectedDashboardIndex}
            selectedHierarchyId={this.props.selectedHierarchyId}
            selectedCategory={this.props.selection?.category}
            color={aTL.color}
            disabled={aTL.disabled}
          />
        );
      });

    // render the selection-boxes and the whole table(s)
    return (
      <table className="datatable fullwidthwhite">
        {circuitItems.length > 0 && (
          <thead>
            <tr>
              {makeSortHeader(
                TABLE_NAME,
                "name",
                <FormattedMessage id="Name" />,
                this.props.tableSort ?? { column: "name", isSortAscending: true },
                this.props.onSort,
              )}
              {showHierachy &&
                makeSortHeader(
                  TABLE_NAME,
                  "parent",
                  <FormattedMessage id="Parent" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["optional"],
                )}
              {showCategory &&
                makeSortHeader(
                  TABLE_NAME,
                  "categories",
                  <FormattedMessage id="Category" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["optional"],
                )}
              {makeSortHeader(
                TABLE_NAME,
                "value",
                <FormattedMessage id="Value" />,
                this.props.tableSort,
                this.props.onSort,
                ["value-col", "center"],
              )}
            </tr>
          </thead>
        )}
        <tbody>{circuitItems}</tbody>
      </table>
    );
  }

  public render() {
    // we need to prepare the values we are using BEFORE calling getfilteredSourtedGroups -> otherwise
    // we would sort "old" values
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);

    // check if a hierarchy and/or a category is available
    const hasHierachy = this.props.tariffCircuits.some((curItem) => isSet(curItem.parent));
    const hasCategory = this.props.tariffCircuits.some((curItem) => isSet(curItem.categories));

    const energySources = this.props.selectedEnergySource
      ? [this.props.selectedEnergySource]
      : (tcModel.getEnergySources(false) as string[]);

    const tlInfosSorted = energySources.map((anEnergySource) => {
      return this.getSelectedTariffCircuits(
        anEnergySource,
        false, // show only selected tariffs
        this.props.selectedHierarchyId === undefined, // allow all Hierarchies
        false, // ignore categories
        false, // show all children
        false, // ignore coloring
      ).tlEntries;
    });

    // map the energy-sources to React-Components
    // only render the headline if more than one group is available!
    const circuitGroups = energySources.map((curSource, curIndex) => {
      return (
        <React.Fragment key={curSource}>
          {energySources.length > 1 && tlInfosSorted[curIndex].length > 0 && (
            <h2 className="sourceheading">{getEnergySourceName(curSource)}</h2>
          )}
          {this.renderSortedListGroup(tlInfosSorted[curIndex], hasHierachy, hasCategory)}
        </React.Fragment>
      );
    });

    // render all tables for all energy-sources
    return (
      <div id="tariffcircuits">
        {circuitGroups.length ? (
          circuitGroups
        ) : (
          <FormattedMessage id="No tariff circuits present." />
        )}
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(TariffCircuitListComponent));
